/**
 * Tenant admin — Order Workflow editor.
 *
 * The matrix lets the admin:
 *   • Toggle which canonical statuses appear in the flow
 *   • Edit the label + colour shown to customers/staff
 *   • Pick which actor roles can advance each status (matrix)
 *   • Pick the next allowed statuses per step (multi-select)
 *
 * TENANT_ADMIN is implicitly allowed everywhere (super-power),
 * so the matrix only stores the OTHER actors.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, RotateCcw, Power } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminWorkflow() {
    const [wf, setWf] = useState(null);
    const [actorRoles, setActorRoles] = useState([]);
    const [busy, setBusy] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setBusy(true);
        try {
            const { data } = await api.get("/admin/workflow");
            setWf(data.workflow);
            setActorRoles(data.actor_roles);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load workflow");
        } finally {
            setBusy(false);
        }
    };
    useEffect(() => {
        let cancelled = false;
        (async () => { if (!cancelled) await load(); })();
        return () => { cancelled = true; };
    }, []);

    const updateStep = (slug, patch) => {
        setWf((w) => ({
            ...w,
            steps: w.steps.map((s) => (s.slug === slug ? { ...s, ...patch } : s)),
        }));
    };
    const toggleActor = (slug, actor) => {
        const step = wf.steps.find((s) => s.slug === slug);
        const has = step.allowed_actors.includes(actor);
        updateStep(slug, {
            allowed_actors: has
                ? step.allowed_actors.filter((a) => a !== actor)
                : [...step.allowed_actors, actor],
        });
    };
    const toggleNext = (slug, target) => {
        const step = wf.steps.find((s) => s.slug === slug);
        const has = step.next_statuses.includes(target);
        updateStep(slug, {
            next_statuses: has
                ? step.next_statuses.filter((n) => n !== target)
                : [...step.next_statuses, target],
        });
    };
    const toggleEnabled = (slug) => {
        // PLACED is the entry status — disabling it would brick the
        // checkout flow, so it's forced on.
        if (slug === "PLACED") return;
        const step = wf.steps.find((s) => s.slug === slug);
        const nextEnabled = !step.enabled;
        // When disabling, also strip the slug from every other step's
        // next_statuses so a transition to a disabled status can't be
        // saved (the backend validator would 400 us otherwise).
        setWf((w) => ({
            ...w,
            steps: w.steps.map((s) => {
                if (s.slug === slug) return { ...s, enabled: nextEnabled };
                if (!nextEnabled) return { ...s, next_statuses: s.next_statuses.filter((n) => n !== slug) };
                return s;
            }),
        }));
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.put("/admin/workflow", wf);
            setWf(data.workflow);
            toast.success("Workflow saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const reset = async () => {
        if (!window.confirm("Reset to the platform default workflow? Your customisations will be lost.")) return;
        setSaving(true);
        try {
            const { data } = await api.post("/admin/workflow/reset");
            setWf(data.workflow);
            toast.success("Reset to defaults");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally { setSaving(false); }
    };

    if (busy || !wf) {
        return <div className="py-20 text-center"><Loader2 className="inline animate-spin" /></div>;
    }

    return (
        <div>
            <div className="flex items-start justify-between gap-3 mb-4">
                <div>
                    <h1 className="text-2xl font-bold">Order Workflow</h1>
                    <p className="text-sm text-muted-foreground mt-0.5 max-w-2xl">
                        Choose which statuses an order moves through, what each is labelled, and <span className="font-semibold">who</span> can advance it.
                        Tenant admin can always update any status (super-power) — the matrix only controls the OTHER actors.
                    </p>
                </div>
                <div className="flex gap-2 shrink-0">
                    <button onClick={reset} disabled={saving} className="rounded-xl bg-card border border-border px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="wf-reset">
                        <RotateCcw size={13} /> Reset
                    </button>
                    <button onClick={save} disabled={saving} className="rounded-xl bg-primary text-primary-foreground px-4 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="wf-save">
                        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                </div>
            </div>

            <div className="overflow-auto bg-card rounded-2xl border border-border" data-testid="wf-matrix">
                <table className="w-full text-sm min-w-[920px]">
                    <thead className="bg-muted/60 text-left sticky top-0">
                        <tr>
                            <Th className="w-[200px]">Status</Th>
                            <Th className="w-[140px]">Label / Colour</Th>
                            {actorRoles.map((a) => (
                                <Th key={a} className="text-center">{prettyActor(a)}</Th>
                            ))}
                            <Th className="w-[260px]">Can advance to</Th>
                            <Th className="text-center w-[90px]">Enabled</Th>
                        </tr>
                    </thead>
                    <tbody>
                        {wf.steps.map((s) => {
                            const isPlaced = s.slug === "PLACED";
                            const disabled = s.enabled === false;
                            return (
                            <tr key={s.slug} className={`border-t border-border ${disabled ? "opacity-50" : ""}`} data-testid={`wf-row-${s.slug}`}>
                                <Td>
                                    <span className="px-2 py-1 rounded-md text-[10px] font-bold tracking-wider uppercase text-white" style={{ background: s.color }}>{s.slug}</span>
                                </Td>
                                <Td>
                                    <input
                                        data-testid={`wf-label-${s.slug}`}
                                        value={s.label}
                                        onChange={(e) => updateStep(s.slug, { label: e.target.value })}
                                        className="bg-muted rounded-lg px-2 py-1 w-full text-xs outline-none focus:ring-2 ring-primary/30"
                                        disabled={disabled}
                                    />
                                    <input
                                        type="color"
                                        value={s.color}
                                        onChange={(e) => updateStep(s.slug, { color: e.target.value })}
                                        className="mt-1 w-full h-6 rounded cursor-pointer"
                                        disabled={disabled}
                                    />
                                </Td>
                                {actorRoles.map((a) => (
                                    <Td key={a} className="text-center">
                                        <input
                                            type="checkbox"
                                            checked={s.allowed_actors.includes(a)}
                                            onChange={() => toggleActor(s.slug, a)}
                                            data-testid={`wf-actor-${s.slug}-${a}`}
                                            disabled={disabled}
                                            className="w-4 h-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                                        />
                                    </Td>
                                ))}
                                <Td>
                                    <div className="flex flex-wrap gap-1">
                                        {wf.steps.filter((t) => t.slug !== s.slug && t.enabled !== false).map((t) => (
                                            <button
                                                key={t.slug}
                                                data-testid={`wf-next-${s.slug}-${t.slug}`}
                                                onClick={() => toggleNext(s.slug, t.slug)}
                                                disabled={disabled}
                                                className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${s.next_statuses.includes(t.slug) ? "text-white" : "bg-muted text-muted-foreground hover:bg-muted/80"} disabled:cursor-not-allowed`}
                                                style={s.next_statuses.includes(t.slug) ? { background: t.color } : {}}
                                            >{t.slug}</button>
                                        ))}
                                    </div>
                                </Td>
                                <Td className="text-center">
                                    <button
                                        onClick={() => toggleEnabled(s.slug)}
                                        disabled={isPlaced}
                                        title={isPlaced ? "PLACED is the entry status and cannot be disabled" : disabled ? "Click to enable" : "Click to disable"}
                                        className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-bold transition ${disabled ? "bg-muted text-muted-foreground" : "bg-emerald-100 text-emerald-700"} ${isPlaced ? "opacity-60 cursor-not-allowed" : "hover:opacity-80"}`}
                                        data-testid={`wf-toggle-${s.slug}`}
                                    >
                                        <Power size={11} />
                                        {disabled ? "OFF" : "ON"}
                                    </button>
                                </Td>
                            </tr>
                        );})}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

const prettyActor = (a) => ({ CUSTOMER: "Customer", MERCHANT: "Merchant", ZONE_ADMIN: "Zone Admin", RIDER: "Rider" }[a] || a);
const Th = ({ children, className = "" }) => <th className={`px-3 py-2.5 text-[11px] uppercase tracking-wider font-bold text-muted-foreground ${className}`}>{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-3 py-2.5 align-top ${className}`}>{children}</td>;
